import React, { useMemo } from 'react';
import { categorizeEmployee, formatPercentage, safeGet, workforceHeadcountFromCoverage } from '../../utils/trainingUtils';
import { TrainingIcon } from '../TrainingIcon';

/**
 * Detailed Training Coverage Breakdown — per-gender Health & Safety and Skill
 * Development coverage for employees and workers, with per-column totals.
 */
export const DetailedTrainingCoverageBreakdown = ({ trainingData, traineeList, financialYear, financialYearId, categoryCoverage, categoryIds }) => {
  const coverageAnalysis = useMemo(() => {
    const analysis = {
      employees: {
        male: { total: 0, healthSafety: new Set(), skillDevelopment: new Set() },
        female: { total: 0, healthSafety: new Set(), skillDevelopment: new Set() }
      },
      workers: {
        male: { total: 0, healthSafety: new Set(), skillDevelopment: new Set() },
        female: { total: 0, healthSafety: new Set(), skillDevelopment: new Set() }
      }
    };

    if (categoryCoverage) {
      const workforce = workforceHeadcountFromCoverage(categoryCoverage);
      analysis.employees.male.total = workforce.employees.male;
      analysis.employees.female.total = workforce.employees.female;
      analysis.workers.male.total = workforce.workers.male;
      analysis.workers.female.total = workforce.workers.female;
    } else if (Array.isArray(traineeList)) {
      traineeList.forEach(trainee => {
        if (!trainee) return;

        const category = categorizeEmployee(trainee);
        const gender = trainee.gender?.toLowerCase() || 'unknown';

        if (category === 'Employees other than BoD and KMPs' || category === 'Key Managerial Personnel') {
          if (gender === 'male') analysis.employees.male.total++;
          else if (gender === 'female') analysis.employees.female.total++;
        } else if (category === 'Workers') {
          if (gender === 'male') analysis.workers.male.total++;
          else if (gender === 'female') analysis.workers.female.total++;
        }
      });
    }

    const healthSafetyId = categoryIds?.healthSafety;
    const skillDevelopmentId = categoryIds?.skillDevelopment;

    if (trainingData && Array.isArray(trainingData)) {
      trainingData.forEach(training => {
        if (!training) return;

        const attendants = training.attendantUsers || [];
        const trainingCategoryIds = (training.categories || []).map(category => category?.id).filter(Boolean);

        attendants.forEach(attendant => {
          if (!attendant) return;

          const employeeCategory = categorizeEmployee(attendant);
          const gender = attendant.gender?.toLowerCase() || 'unknown';
          const employeeId = attendant.id;
          
          if (!employeeId) return;

          let targetCategory = null;
          if (employeeCategory === 'Employees other than BoD and KMPs' ||
              employeeCategory === 'Key Managerial Personnel') {
            targetCategory = 'employees';
          } else if (employeeCategory === 'Workers') {
            targetCategory = 'workers';
          }

          if (!targetCategory) return;

          if (healthSafetyId != null && trainingCategoryIds.includes(healthSafetyId)) {
            if (gender === 'male') {
              analysis[targetCategory].male.healthSafety.add(employeeId);
            } else if (gender === 'female') {
              analysis[targetCategory].female.healthSafety.add(employeeId);
            }
          }

          if (skillDevelopmentId != null && trainingCategoryIds.includes(skillDevelopmentId)) {
            if (gender === 'male') {
              analysis[targetCategory].male.skillDevelopment.add(employeeId);
            } else if (gender === 'female') {
              analysis[targetCategory].female.skillDevelopment.add(employeeId);
            }
          }
        });
      });
    }

    ['employees', 'workers'].forEach(category => {
      ['male', 'female'].forEach(gender => {
        if (analysis[category] && analysis[category][gender]) {
          analysis[category][gender].healthSafetyCount = analysis[category][gender].healthSafety?.size || 0;
          analysis[category][gender].skillDevelopmentCount = analysis[category][gender].skillDevelopment?.size || 0;
          delete analysis[category][gender].healthSafety;
          delete analysis[category][gender].skillDevelopment;
        }
      });
    });

    return analysis;
  }, [trainingData, traineeList, categoryCoverage, categoryIds]);

  return (
    <div className="trd-section">
      <div className="trd-section-head">
        <span className="trd-section-icon"><TrainingIcon name="grid" size={18} /></span>
        <div>
          <h2 className="trd-section-title">Detailed Training Coverage Breakdown</h2>
        </div>
      </div>

      <div className="trd-block">
        <h3 className="trd-block-title"><TrainingIcon name="users" size={15} />Employees including KMPs</h3>

        <div className="trd-table-wrap">
          <table className="trd-table">
            <thead>
              <tr>
                <th className="trd-th-label">Category</th>
                <th>Total (A)</th>
                <th>
                  <div>Health &amp; Safety</div>
                  <span className="trd-th-sub">No. (B) | % (B/A)</span>
                </th>
                <th>
                  <div>Skill Development</div>
                  <span className="trd-th-sub">No. (C) | % (C/A)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="trd-tr">
                <td className="trd-td-label">Male</td>
                <td>{safeGet(coverageAnalysis, 'employees.male.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'employees.male.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.male.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'employees.male.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'employees.male.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.male.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'employees.male.total')
                  )}</span>
                </td>
              </tr>
              <tr className="trd-tr">
                <td className="trd-td-label">Female</td>
                <td>{safeGet(coverageAnalysis, 'employees.female.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'employees.female.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.female.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'employees.female.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'employees.female.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.female.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'employees.female.total')
                  )}</span>
                </td>
              </tr>
              <tr className="trd-tr-total">
                <td className="trd-td-label">Total</td>
                <td>{safeGet(coverageAnalysis, 'employees.male.total') + safeGet(coverageAnalysis, 'employees.female.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'employees.male.healthSafetyCount') + safeGet(coverageAnalysis, 'employees.female.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.male.healthSafetyCount') + safeGet(coverageAnalysis, 'employees.female.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'employees.male.total') + safeGet(coverageAnalysis, 'employees.female.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'employees.male.skillDevelopmentCount') + safeGet(coverageAnalysis, 'employees.female.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'employees.male.skillDevelopmentCount') + safeGet(coverageAnalysis, 'employees.female.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'employees.male.total') + safeGet(coverageAnalysis, 'employees.female.total')
                  )}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="trd-block">
        <h3 className="trd-block-title"><TrainingIcon name="briefcase" size={15} />Workers</h3>

        <div className="trd-table-wrap">
          <table className="trd-table">
            <thead>
              <tr>
                <th className="trd-th-label">Category</th>
                <th>Total (A)</th>
                <th>
                  <div>Health &amp; Safety</div>
                  <span className="trd-th-sub">No. (B) | % (B/A)</span>
                </th>
                <th>
                  <div>Skill Development</div>
                  <span className="trd-th-sub">No. (C) | % (C/A)</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="trd-tr">
                <td className="trd-td-label">Male</td>
                <td>{safeGet(coverageAnalysis, 'workers.male.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'workers.male.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.male.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'workers.male.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'workers.male.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.male.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'workers.male.total')
                  )}</span>
                </td>
              </tr>
              <tr className="trd-tr">
                <td className="trd-td-label">Female</td>
                <td>{safeGet(coverageAnalysis, 'workers.female.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'workers.female.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.female.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'workers.female.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'workers.female.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.female.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'workers.female.total')
                  )}</span>
                </td>
              </tr>
              <tr className="trd-tr-total">
                <td className="trd-td-label">Total</td>
                <td>{safeGet(coverageAnalysis, 'workers.male.total') + safeGet(coverageAnalysis, 'workers.female.total')}</td>
                <td>
                  <span className="trd-num trd-num--blue">{safeGet(coverageAnalysis, 'workers.male.healthSafetyCount') + safeGet(coverageAnalysis, 'workers.female.healthSafetyCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--blue">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.male.healthSafetyCount') + safeGet(coverageAnalysis, 'workers.female.healthSafetyCount'),
                    safeGet(coverageAnalysis, 'workers.male.total') + safeGet(coverageAnalysis, 'workers.female.total')
                  )}</span>
                </td>
                <td>
                  <span className="trd-num trd-num--green">{safeGet(coverageAnalysis, 'workers.male.skillDevelopmentCount') + safeGet(coverageAnalysis, 'workers.female.skillDevelopmentCount')}</span>
                  <span className="trd-sep">|</span>
                  <span className="trd-num trd-num--green">{formatPercentage(
                    safeGet(coverageAnalysis, 'workers.male.skillDevelopmentCount') + safeGet(coverageAnalysis, 'workers.female.skillDevelopmentCount'),
                    safeGet(coverageAnalysis, 'workers.male.total') + safeGet(coverageAnalysis, 'workers.female.total')
                  )}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
